'use client';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ImageProps } from '@repo/ui/src/types/image';
import Image from 'next/image';
import { classNames } from '@repo/ui/src/utils/classNames';
import { useIsMD, useIsSM } from '@repo/ui/src/utils/useMediaQuery';

export interface TextIconSingleBoxes {
  label: string;
  name: string;
  icon: ImageProps;
  description?: string;
}

export interface TextIconComboBox {
  name: string;
  seperatorText?: string;
  content: Array<{ label: string; icon: any }>; //TODO: Type the icon prop better
}
export interface TextIconBoxesProps {
  textIconSingleBoxes: Array<TextIconSingleBoxes>;
  textIconComboBox: TextIconComboBox;
  darkMode?: boolean;
}

const TextIconBoxes = ({ textIconSingleBoxes, textIconComboBox, darkMode }: TextIconBoxesProps) => {
  const isMD = useIsMD();
  const isSM = useIsSM();
  const bgColor = darkMode ? 'tw-bg-neutral-600' : 'tw-bg-warm-200';

  const formattedSingleBoxes = textIconSingleBoxes?.map(({ name, ...rest }) => ({
    name,
    content: [rest],
  }));
  const combinedContentBoxes: Array<TextIconComboBox> = [textIconComboBox, ...formattedSingleBoxes];

  return (
    <ul
      className={classNames(
        'tw-grid tw-w-full md:tw-w-auto tw-justify-center tw-grid-rows-[repeat(2, auto)] tw-gap-6 tw-mx-auto tw-mt-18 lg:tw-mt-20 tw-list-none tw-p-0',
        'md:tw-order-2 xl:tw-pt-10',
        isMD && 'tw-grid-rows-[auto]',
      )}
      style={{
        gridTemplateColumns: isMD
          ? `max-content repeat(${textIconSingleBoxes.length}, auto)`
          : `repeat(${textIconSingleBoxes.length}, 1fr)`,
      }}
    >
      {combinedContentBoxes.map(({ name, seperatorText, content }, index) => (
        <li
          key={name}
          className={classNames(
            'tw-flex tw-flex-col tw-py-4 tw-px-5 sm:tw-py-10 md:tw-px-6',
            index === 0 && 'tw-row-start-2 md:tw-row-start-auto',
            bgColor,
          )}
          style={{
            gridColumn: index === 0 && !isMD ? `span ${textIconSingleBoxes.length}` : undefined,
          }}
        >
          <h6
            className={classNames(
              'tw-text-center lg:tw-pb-6 md:tw-mb-6 tra-text-web-heading-7',
              darkMode ? 'tw-text-warm-400' : 'tw-text-neutral-500',
            )}
          >
            {name}
          </h6>

          <ul className="tw-flex tw-justify-center tw-mt-3 md:tw-mt-auto tw-list-none tw-p-0">
            {content.map(({ label, icon }, idx) => (
              <li key={label} className="tw-flex tw-items-center">
                <div className="tw-flex tw-flex-col tw-items-center">
                  <div className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 sm:tw-w-14 sm:tw-h-14">
                    <Image
                      src={buildImageSrc(icon, isSM ? 56 : 32)}
                      width={isSM ? 56 : 32}
                      height={isSM ? 56 : 32}
                      objectFit='cover'
                      alt={icon.altTag}
                      className="tw-w-full tw-h-full tw-max-w-14 tw-max-h-14"
                      aria-hidden="true"
                    />
                  </div>
                  <h6
                    className={classNames(
                      'tw-pt-4 tra-text-web-heading-6 tw-text-nowrap',
                      darkMode ? 'tw-text-warm-400' : 'tw-text-neutral-500',
                    )}
                  >
                    {label}
                  </h6>
                </div>

                {content.length > 1 && idx < content.length - 1 ? (
                  <div className="tw-relative tw-mx-[5vw] sm:tw-mx-12 tw-w-px tw-h-full tw-bg-neutral-700">
                    <div className="tw-absolute tw-top-1/2 tw-left-1/2 tw-transform -tw-translate-x-1/2 -tw-translate-y-1/2">
                      <p
                        className={classNames(
                          'tra-text-web-body-m tw-mb-0 tw-uppercase',
                          darkMode ? 'tw-text-warm-400' : 'tw-text-neutral-500',
                          bgColor,
                        )}
                      >
                        {seperatorText}
                      </p>
                    </div>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
};

export default TextIconBoxes;
